import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Bold,
  Italic,
  Heading,
  List,
  ListOrdered,
  Link2,
  Undo2,
  Redo2,
} from 'lucide-react'

// A dependency-free rich-text editor built on contentEditable + document.execCommand.
// It emits HTML that the backend re-sanitises before storage, so nothing here is
// trusted for rendering — it is purely an authoring convenience for admins.
export default function RichTextEditor({ value = '', onChange, placeholder = 'Write the policy text…' }) {
  const editorRef = useRef(null)
  const [focused, setFocused] = useState(false)

  // Only push the incoming HTML into the DOM when it differs from what is
  // already there, otherwise the caret jumps on every keystroke.
  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    if (el.innerHTML !== (value ?? '')) {
      el.innerHTML = value ?? ''
    }
  }, [value])

  const emit = useCallback(() => {
    const el = editorRef.current
    if (!el) return
    onChange?.(el.innerHTML)
  }, [onChange])

  const exec = useCallback((command, arg) => {
    editorRef.current?.focus()
    document.execCommand(command, false, arg ?? null)
    emit()
  }, [emit])

  const insertLink = useCallback(() => {
    const url = window.prompt('Enter a URL to link:')
    if (url) exec('createLink', url)
  }, [exec])

  const TOOLBAR = [
    { icon: Bold, title: 'Bold', run: () => exec('bold') },
    { icon: Italic, title: 'Italic', run: () => exec('italic') },
    { icon: Heading, title: 'Heading', run: () => exec('formatBlock', 'h3') },
    { icon: List, title: 'Bullet list', run: () => exec('insertUnorderedList') },
    { icon: ListOrdered, title: 'Numbered list', run: () => exec('insertOrderedList') },
    { icon: Link2, title: 'Insert link', run: insertLink },
    { icon: Undo2, title: 'Undo', run: () => exec('undo') },
    { icon: Redo2, title: 'Redo', run: () => exec('redo') },
  ]

  return (
    <div
      className={`overflow-hidden rounded-lg border bg-white transition-colors ${
        focused ? 'border-rapid-orange ring-2 ring-rapid-orange/20' : 'border-gray-200'
      }`}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-100 bg-gray-50 px-2 py-1.5">
        {TOOLBAR.map(({ icon: Icon, title, run }) => (
          <button
            key={title}
            type="button"
            title={title}
            aria-label={title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={run}
            className="rounded-md p-1.5 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
          >
            <Icon size={15} strokeWidth={2} aria-hidden />
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        className="policy-richtext min-h-[220px] max-h-[440px] overflow-y-auto px-4 py-3 text-sm text-gray-800 outline-none"
      />
    </div>
  )
}
